import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import type {
  DetectionPerDomainData,
  DetectionPerEngineData,
  DetectionVolumeData,
  PirateSourceDistributionData,
} from "@nexus/widgets";
import type { DataResolver } from "@/lib/dashboard/resolverTypes";
import { getFilterValue, getFilterValues, getTenantIds } from "./shared";

// ── Detection Volume ──────────────────────────────────────────────────────────

const DETECTION_VOLUME_QUERY = gql`
  query DetectionVolumeTimeseries(
    $from_ts: String!
    $to_ts: String!
    $interval_minutes: Int
    $tenant_ids: [Int!]
    $detection_domain: String
    $event_id: String
  ) {
    detection_volume_timeseries(
      from_ts: $from_ts
      to_ts: $to_ts
      interval_minutes: $interval_minutes
      tenant_ids: $tenant_ids
      detection_domain: $detection_domain
      event_id: $event_id
    ) {
      bucket
      count
    }
  }
`;

type DetectionVolumeResponse = {
  detection_volume_timeseries: Array<{ bucket: string; count: number }>;
};

function normalizeDetectionVolume(raw: unknown): DetectionVolumeData {
  const data = raw as DetectionVolumeResponse;
  return {
    points: (data.detection_volume_timeseries ?? []).map((p) => ({
      timestamp: p.bucket,
      count: Number(p.count),
    })),
  };
}

// ── Detection per Domain ──────────────────────────────────────────────────────

const DETECTION_PER_DOMAIN_QUERY = gql`
  query DetectionPerDomain($from_ts: String!, $to_ts: String!, $tenant_ids: [Int!], $limit: Int) {
    detection_per_domain(from_ts: $from_ts, to_ts: $to_ts, tenant_ids: $tenant_ids, limit: $limit) {
      domain
      total_detections
      actionable_count
      trend {
        bucket
        count
      }
      expected_count
    }
  }
`;

type DetectionPerDomainResponse = {
  detection_per_domain: Array<{
    domain: string;
    total_detections: number;
    actionable_count: number;
    trend: Array<{ bucket: string; count: number }>;
    expected_count: number | null;
  }>;
};

function normalizeDetectionPerDomain(raw: unknown): DetectionPerDomainData {
  const data = raw as DetectionPerDomainResponse;
  return {
    domains: (data.detection_per_domain ?? []).map((d) => ({
      domain: d.domain,
      total_detections: Number(d.total_detections),
      actionable_count: Number(d.actionable_count),
      trend: (d.trend ?? []).map((p) => ({ bucket: p.bucket, count: Number(p.count) })),
      expected_count: d.expected_count != null ? Number(d.expected_count) : null,
    })),
  };
}

// ── Pirate Source Distribution ────────────────────────────────────────────────

const PIRATE_SOURCE_DISTRIBUTION_QUERY = gql`
  query PirateSourceDistribution(
    $from_ts: String!
    $to_ts: String!
    $protected_event_id: Int!
    $tenant_ids: [Int!]
    $detection_domains: [String!]
  ) {
    pirate_source_distribution(
      from_ts: $from_ts
      to_ts: $to_ts
      protected_event_id: $protected_event_id
      tenant_ids: $tenant_ids
      detection_domains: $detection_domains
    ) {
      source_type
      count
      total_count
      percentage
    }
  }
`;

type PirateSourceDistributionResponse = {
  pirate_source_distribution: Array<{
    source_type: "ott" | "sat" | "others" | "unknown";
    count: number;
    total_count: number;
    percentage: number;
  }>;
};

function normalizePirateSourceDistribution(raw: unknown): PirateSourceDistributionData {
  const data = raw as PirateSourceDistributionResponse;
  return {
    entries: (data.pirate_source_distribution ?? []).map((entry) => ({
      source_type: entry.source_type,
      count: Number(entry.count),
      total_count: Number(entry.total_count),
      percentage: Number(entry.percentage),
    })),
  };
}

// ── Detection per Engine ──────────────────────────────────────────────────────

const DETECTION_PER_ENGINE_QUERY = gql`
  query DetectionPerEngine($from_ts: String!, $to_ts: String!, $tenant_ids: [Int!], $limit: Int) {
    detection_per_engine(from_ts: $from_ts, to_ts: $to_ts, tenant_ids: $tenant_ids, limit: $limit) {
      engine_id
      engine_label
      total_detections
      actionable_count
      trend {
        bucket
        count
      }
      expected_count
    }
  }
`;

type DetectionPerEngineResponse = {
  detection_per_engine: Array<{
    engine_id: number;
    engine_label: string;
    total_detections: number;
    actionable_count: number;
    trend: Array<{ bucket: string; count: number }>;
    expected_count: number | null;
  }>;
};

function normalizeDetectionPerEngine(raw: unknown): DetectionPerEngineData {
  const data = raw as DetectionPerEngineResponse;
  return {
    engines: (data.detection_per_engine ?? []).map((e) => ({
      engine_id: Number(e.engine_id),
      engine_label: e.engine_label,
      total_detections: Number(e.total_detections),
      actionable_count: Number(e.actionable_count),
      trend: (e.trend ?? []).map((p) => ({ bucket: p.bucket, count: Number(p.count) })),
      expected_count: e.expected_count != null ? Number(e.expected_count) : null,
    })),
  };
}

// ── Resolvers ─────────────────────────────────────────────────────────────────

export const detectionDataSources: Record<string, DataResolver> = {
  "detection-volume": {
    contract: { name: "detection-volume", version: 1 },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
      detection_domain: getFilterValue(filters, "detection_domain"),
      event_id: getFilterValue(filters, "event_id"),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<DetectionVolumeResponse>(DETECTION_VOLUME_QUERY, params);
      return normalizeDetectionVolume(raw);
    },
  },

  "detection-per-domain": {
    contract: { name: "detection-per-domain", version: 1 },
    // deliberately no domain filter — the list always shows all domains
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<DetectionPerDomainResponse>(
        DETECTION_PER_DOMAIN_QUERY,
        params,
      );
      return normalizeDetectionPerDomain(raw);
    },
  },

  "pirate-source-distribution": {
    contract: { name: "pirate-source-distribution", version: 1 },
    buildParams: (_requestParams, filters) => {
      const protectedEventId = getFilterValue(filters, "protected_event_id");
      return {
        from_ts: filters.timeRange.from,
        to_ts: filters.timeRange.to,
        protected_event_id: protectedEventId ? Number(protectedEventId) : 0,
        tenant_ids: getTenantIds(filters),
        detection_domains: getFilterValues(filters, "detection_domain"),
      };
    },
    fetcher: async (params, client: GraphQLClient) => {
      if (!params.protected_event_id) return { entries: [] } satisfies PirateSourceDistributionData;
      const raw = await client.request<PirateSourceDistributionResponse>(
        PIRATE_SOURCE_DISTRIBUTION_QUERY,
        params,
      );
      return normalizePirateSourceDistribution(raw);
    },
  },

  "detection-per-engine": {
    contract: { name: "detection-per-engine", version: 1 },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<DetectionPerEngineResponse>(
        DETECTION_PER_ENGINE_QUERY,
        params,
      );
      return normalizeDetectionPerEngine(raw);
    },
  },
};
